import { makeStyles } from '@mui/styles';
import Checkbox from 'apollo-react/components/Checkbox';
import IconButton from 'apollo-react/components/IconButton';
import Modal from 'apollo-react/components/Modal';
import Table, {
  compareNumbers,
  compareStrings,
  createStringSearchFilter,
} from 'apollo-react/components/Table';
import Tooltip from 'apollo-react/components/Tooltip';
import Export from 'apollo-react-icons/Export';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getFormHistoryIsOpen, getFormHistoryRows } from './FormHistorySelectors';
import {
  clearFormHistoryModal,
  compareForms,
  exportForm,
  fetchFormHistoryFromServer,
  setOpenFormHistoryModal,
} from '../../actions/FormHistoryActions';
import DateCell from '../../components/CommonTableCells/DateCell';
import DatePickerFilter from '../../components/TableFilters/DatePickerFilter';
import TextFieldFilter from '../../components/TableFilters/TextInputFilter';
import { useFormSelector } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { MAX_QUANTITY_SELECTED_ROWS } from '../../form-designer/constants/editor.constants';
import { FORM_KEYS } from '../../form-designer/constants/form-manager.constants';
import { getFormKey } from '../../form-designer/selectors/form-manager.selectors';
import { createCustomNumberSearchFilter } from '../../form-designer/utils/controls/ssGrid.utils';
import {
  createCustomCompareDatesForTable,
  createCustomDateFilter,
  getCompareDetails,
} from '../../utils/formHistoryUtils';

const useStyles = makeStyles({
  content: {
    border: 'solid 1px #e9e9e9',
    '& thead > tr > th:nth-child(1) > svg': {
      display: 'none !important',
    },
    '& thead > tr > th:last-child > svg': {
      display: 'none !important',
    },
  },
  modal: {
    maxWidth: 'unset',
  },
});

const CheckboxCell = ({ row: { selectedRows, OPS_ID: id, handleSelectRow } }) => (
  <div style={{ width: 16 }}>
    <Checkbox
      checked={selectedRows.includes(id)}
      disabled={selectedRows.length === 2 && !selectedRows.includes(id)}
      onChange={() => handleSelectRow(id)}
    />
  </div>
);

const ExportButtonCell = ({ row }) => {
  const translateString = useTranslation();
  const handleClick = () => {
    row.onExportForm(row);
  };

  return (
    <Tooltip title={translateString('Export')} placement="bottom">
      <IconButton onClick={handleClick} aria-label={translateString('Export')}>
        <Export />
      </IconButton>
    </Tooltip>
  );
};

const columns = [
  {
    customCell: CheckboxCell,
  },
  {
    header: 'Version',
    accessor: 'OPS_VERSION',
    sortFunction: compareNumbers,
    filterFunction: createCustomNumberSearchFilter('OPS_VERSION'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Status',
    accessor: 'STATUS',
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('STATUS'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Published Date',
    accessor: 'OPS_PUBLISHED_DATE',
    sortFunction: createCustomCompareDatesForTable,
    customCell: DateCell,
    filterFunction: createCustomDateFilter('OPS_PUBLISHED_DATE'),
    filterComponent: DatePickerFilter,
  },
  {
    header: 'Published By',
    accessor: 'OPS_PUBLISHED_BY',
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('OPS_PUBLISHED_BY'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Published Comment',
    accessor: 'OPS_PUBLISH_COMMENT',
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('OPS_PUBLISH_COMMENT'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Unpublished Date',
    accessor: 'OPS_UNPUBLISHED_DATE',
    sortFunction: createCustomCompareDatesForTable,
    customCell: DateCell,
    filterFunction: createCustomDateFilter('OPS_UNPUBLISHED_DATE'),
    filterComponent: DatePickerFilter,
  },
  {
    header: 'Unpublished By',
    accessor: 'OPS_UNPUBLISHED_BY',
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('OPS_UNPUBLISHED_BY'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Unpublished Comment',
    accessor: 'OPS_UNPUBLISH_COMMENT',
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('OPS_UNPUBLISH_COMMENT'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Release Version',
    accessor: 'OPS_RELEASE_VERSION',
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('OPS_RELEASE_VERSION'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Release Type',
    accessor: 'OPS_RELEASE_TYPE',
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('OPS_RELEASE_TYPE'),
    filterComponent: TextFieldFilter,
  },
  {
    customCell: ExportButtonCell,
  },
];

const FormHistoryDialogComponent = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const translateString = useTranslation();
  const formKey = useFormSelector(getFormKey, { formKey: FORM_KEYS.EDITOR });
  const isOpen = useFormSelector(getFormHistoryIsOpen);
  const rows = useFormSelector(getFormHistoryRows);
  const [isLoading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const rowsPerPageOptions = [5, 10, 15, 'All'];

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      await dispatch(fetchFormHistoryFromServer(formKey));
      setLoading(false);
    }

    isOpen && formKey && fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onExportForm = (rowDetail) => {
    const fileName = `${rowDetail.OPS_KEY}_${translateString('version')}_${rowDetail.OPS_VERSION}`;
    dispatch(exportForm({ formId: rowDetail.OPS_ID, fileName }));
  };

  const onCompare = (selectedRows, rows) => {
    const { baselineVersion, compareToVersion } = getCompareDetails(selectedRows, rows);

    dispatch(compareForms({ formKey, baselineVersion, compareToVersion }));
  };

  const handleModalClose = () => {
    dispatch(setOpenFormHistoryModal(false));
  };

  // After the modal fades out, clear the state
  const handleModalExited = () => {
    dispatch(clearFormHistoryModal());
    setSelectedRows([]);
  };

  const handleSelectRow = (id) => {
    setSelectedRows((selectedRows) => {
      let newSelectedRows = [...selectedRows];

      if (selectedRows.includes(id)) {
        newSelectedRows = newSelectedRows.filter((rowId) => rowId !== id);
      } else {
        newSelectedRows.push(id);
      }

      return Array.from(new Set(newSelectedRows));
    });
  };

  const buttonProps = [
    {
      variant: 'secondary',
      label: translateString('Close'),
      onClick: () => {
        handleModalClose();
      },
    },
    {
      variant: 'primary',
      label: translateString('Compare'),
      disabled: selectedRows.length !== MAX_QUANTITY_SELECTED_ROWS,
      onClick: () => onCompare(selectedRows, rows),
    },
  ];

  const title = isLoading ? '' : translateString('Form History');

  return (
    <Modal
      buttonProps={buttonProps}
      className={classes.modal}
      disableBackdropClick
      disableEscapeKeyDown
      disablePortal
      onClose={() => handleModalClose()}
      onExited={() => handleModalExited()}
      open={isOpen}
      title={title}
      variant="default"
    >
      <div className={classes.content}>
        <Table
          columns={columns}
          rows={rows.map((row) => ({
            ...row,
            selectedRows,
            handleSelectRow,
            onExportForm,
            translateString,
          }))}
          rowsPerPageOptions={rowsPerPageOptions}
          showFilterIcon
          isLoading={isLoading}
          initialSortedColumn="OPS_VERSION"
          initialSortOrder="desc"
        />
      </div>
    </Modal>
  );
};

export default FormHistoryDialogComponent;
